import { useState, useRef } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { RadioButton, Badge, toast } from '@bsf/force-ui'; // Import toast for notifications
import {
	AwsIcon,
	SmtpIcon,
	MailGunIcon,
	OutlookIcon,
	PostMarkIcon,
	SendGridIcon,
	ZohoIcon,
	GmailIcon,
	BrevoIcon,
	SparkPostIcon,
	ElasticEmailIcon,
	SMTP_COMIcon,
	PepipostIcon,
	MailjetIcon,
} from 'assets/icons';

// Define radio button options with unique 'id's and 'value's
export const CONNECTION_PROVIDERS = [
	{
		id: 2,
		value: 'AWS',
		label: __( 'Amazon SES', 'suremails' ),
		icon: <AwsIcon className="w-6 h-6" />,
	},
	{
		id: 1,
		value: 'SMTP',
		label: __( 'Other SMTP Provider', 'suremails' ),
		icon: <SmtpIcon className="w-6 h-6" />,
	},
	{
		id: 8,
		value: 'Gmail',
		label: __( 'Gmail', 'suremails' ),
		icon: <GmailIcon className="w-6 h-6" />,
		badge: <Badge label="Planned" size="xxs" type="pill" variant="green" />,
	},
	{
		id: 3,
		value: 'Outlook',
		label: __( 'Microsoft Outlook/Office 365', 'suremails' ),
		icon: <OutlookIcon className="w-6 h-6" />,
		badge: <Badge label="Planned" size="xxs" type="pill" variant="green" />,
	},
	{
		id: 4,
		value: 'Mailgun',
		label: __( 'Mailgun', 'suremails' ),
		icon: <MailGunIcon className="w-6 h-6" />,
		badge: <Badge label="Planned" size="xxs" type="pill" variant="green" />,
	},
	{
		id: 5,
		value: 'SendGrid',
		label: __( 'SendGrid', 'suremails' ),
		icon: <SendGridIcon className="w-6 h-6" />,
		badge: <Badge label="Planned" size="xxs" type="pill" variant="green" />,
	},
	{
		id: 6,
		value: 'Postmark',
		label: __( 'Postmark', 'suremails' ),
		icon: <PostMarkIcon className="w-6 h-6" />,
		badge: <Badge label="Planned" size="xxs" type="pill" variant="green" />,
	},
	{
		id: 7,
		value: 'Zoho',
		label: __( 'Zoho Mail', 'suremails' ),
		icon: <ZohoIcon className="w-6 h-6" />,
		badge: <Badge label="Planned" size="xxs" type="pill" variant="green" />,
	},

	{
		id: 9,
		value: 'Brevo',
		label: __( 'Brevo (Sendinblue)', 'suremails' ),
		icon: <BrevoIcon className="w-6 h-6" />,
		badge: <Badge label="Planned" size="xxs" type="pill" variant="green" />,
	},
	{
		id: 10,
		value: 'SparkPost',
		label: __( 'SparkPost', 'suremails' ),
		icon: <SparkPostIcon className="w-6 h-6" />,
		badge: <Badge label="Planned" size="xxs" type="pill" variant="green" />,
	},
	{
		id: 11,
		value: 'Elastic Email',
		label: __( 'Elastic Email', 'suremails' ),
		icon: <ElasticEmailIcon className="w-6 h-6" />,
		badge: <Badge label="Planned" size="xxs" type="pill" variant="green" />,
	},
	{
		id: 12,
		value: 'SMTP.com',
		label: __( 'SMTP.com', 'suremails' ),
		icon: <SMTP_COMIcon className="w-6 h-6" />,
		badge: <Badge label="Planned" size="xxs" type="pill" variant="green" />,
	},
	{
		id: 13,
		value: 'Pepipost',
		label: __( 'Pepipost', 'suremails' ),
		icon: <PepipostIcon className="w-6 h-6" />,
		badge: <Badge label="Planned" size="xxs" type="pill" variant="green" />,
	},
	{
		id: 13,
		value: 'Mailjet',
		label: __( 'Mailjet', 'suremails' ),
		icon: <MailjetIcon className="w-6 h-6" />,
		badge: <Badge label="Planned" size="xxs" type="pill" variant="green" />,
	},
];
export const CONNECTION_PROVIDERS_BY_VALUE = CONNECTION_PROVIDERS.reduce(
	( acc, item ) => {
		acc[ item.value ] = item;
		return acc;
	},
	{}
);

const ProviderList = ( { onSelectProvider } ) => {
	const [ selectedProvider, setSelectedProvider ] = useState( '' );
	const toastRef = useRef( false ); // Ref to track toast display

	/**
	 * Handles the change event when a provider is selected.
	 *
	 * @param {string} value - The value of the selected provider.
	 */
	const handleProviderChange = ( value ) => {
		// Find the selected option from the data
		const selectedOption = CONNECTION_PROVIDERS.find(
			( option ) => option.value === value
		);

		// Check if the selected option has a 'badge' (i.e., "Soon")
		if ( selectedOption && selectedOption.badge ) {
			// Prevent multiple toasts by checking the ref
			if ( ! toastRef.current ) {
				toast.info(
					__( 'This provider is coming soon!', 'suremails' )
				);
				toastRef.current = true;
				setTimeout( () => {
					toastRef.current = false;
				}, 500 );
			}
			return; // Do nothing if the option is marked as "Soon"
		}

		// Proceed if the option does not have a 'badge'
		setSelectedProvider( value );
		onSelectProvider( value );
	};

	return (
		<div className="w-full md:max-w-lg bg-background-primary rounded-xl">
			{ /* RadioButton Group */ }
			<RadioButton.Group
				columns={ 1 }
				value={ selectedProvider } // Make it a controlled component
				onChange={ handleProviderChange }
				className="p-1 rounded-lg bg-background-secondary gap-1"
			>
				{ CONNECTION_PROVIDERS.map( ( option ) => (
					<RadioButton.Button
						key={ option.id }
						borderOn
						value={ option.value }
						icon={ option.icon }
						badgeItem={ option.badge }
						size="md"
						inlineIcon
						buttonWrapperClasses="bg-background-primary border-0"
						label={ {
							heading: option.label,
						} }
					/>
				) ) }
			</RadioButton.Group>
		</div>
	);
};

export default ProviderList;
